import { createSelector } from 'reselect';
import type { RootState } from 'reducers/index';
import type { WeddingThemeReducerStateType } from 'reducers/weddingTheme';
import type { StyledPublicWrapperProps } from 'pages/Public.styles';
import type { WWeddingThemeCustomizationView } from '@zola/svc-web-api-ts-client';

export const getWeddingThemeState = (state: RootState): WeddingThemeReducerStateType =>
  state.weddingTheme;

export const selectWeddingTheme = createSelector(getWeddingThemeState, state => state.theme);

export const selectThemeId = createSelector(getWeddingThemeState, state => state.theme.theme_id);

export const selectThemeKey = createSelector(getWeddingThemeState, state => state.theme.theme_key);

export const selectThemeFonts = createSelector(getWeddingThemeState, state => state.themeFonts);

export const selectDefaultValues = createSelector(
  getWeddingThemeState,
  state => state.theme.customization_default
);

export const selectCustomizationValues = createSelector(
  getWeddingThemeState,
  state => state.theme.customization
);

// Stores draft values from form. Includes overrides to be applied only; no defaults.
export const selectCustomizationDraft = createSelector(
  getWeddingThemeState,
  state => state.customizationDraft
);

const mapCustomizationValuesToProps = (
  values: WWeddingThemeCustomizationView
): StyledPublicWrapperProps => ({
  headerFontFamily: (values.header_font || {}).font_family,
  bodyFontFamily: (values.body_font || {}).font_family,
  backgroundColor: values.primary_background_color,
  headerFontColor: values.header_font_color,
  bodyFontColor: values.body_font_color,
});

// Returns saved values in the form of StyledPublicWrapperProps
export const selectCustomizationValuesToApply = createSelector(getWeddingThemeState, state => {
  const savedValues = state.theme.customization || {};
  return mapCustomizationValuesToProps(savedValues);
});

// Returns draft or saved values in the form of StyledPublicWrapperProps
export const selectDraftOrSavedToApply = createSelector(getWeddingThemeState, state => {
  const savedValues = state.theme.customization || {};
  const mappedSavedValues = mapCustomizationValuesToProps(savedValues);
  const draftValues = state.customizationDraft || {};
  // when on customization dashboard, draft values always populated with keys vs. {}
  const isDraftEmpty = Object.keys(draftValues).length === 0;
  return isDraftEmpty ? mappedSavedValues : draftValues;
});
